package com.fantasytenis.web.screens.survivor

import androidx.compose.runtime.*
import com.fantasytenis.shared.data.survivor.repository.SurvivorLeaderboardResult
import com.fantasytenis.shared.domain.survivor.entity.*
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.hooks.useSurvivorSubscription
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying the leaderboard for a survivor contest with real-time updates.
 */
@Composable
fun SurvivorLeaderboardScreen(contestId: String) {
    val scope = rememberCoroutineScope()
    var contest by remember { mutableStateOf<SurvivorContestEntity?>(null) }
    var leaderboardResult by remember { mutableStateOf<SurvivorLeaderboardResult?>(null) }
    var userEntries by remember { mutableStateOf<List<SurvivorEntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    var statusFilter by remember { mutableStateOf<String?>(null) } // null = all, "alive", "eliminated"
    var lastUpdateTime by remember { mutableStateOf<Long?>(null) }
    val pageSize = 50

    // Initialize realtime connection
    val realtimeState = useRealtime()

    // Subscribe to survivor updates
    useSurvivorSubscription(
        contestId = contestId,
        onLeaderboardUpdate = { update ->
            // Update leaderboard with live data
            leaderboardResult?.let { current ->
                val updatedEntries = current.entries.toMutableList()
                update.entries.forEach { liveEntry ->
                    val index = updatedEntries.indexOfFirst { it.id == liveEntry.entryId }
                    if (index >= 0) {
                        val existingEntry = updatedEntries[index]
                        updatedEntries[index] = existingEntry.copy(
                            status = SurvivorEntryStatus.fromApiValue(liveEntry.status),
                            strikesUsed = liveEntry.strikesUsed,
                            survivedRounds = liveEntry.survivedRounds
                        )
                    }
                }
                leaderboardResult = current.copy(
                    entries = updatedEntries,
                    aliveCount = update.aliveCount
                )
            }
            lastUpdateTime = update.timestamp
        },
        onPickResult = { result ->
            // Show notification or update specific entry
            lastUpdateTime = result.timestamp
        }
    )

    // Load data
    LaunchedEffect(contestId, currentPage, statusFilter) {
        isLoading = true
        error = null

        // Load contest details
        DependencyContainer.getSurvivorContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load leaderboard
        DependencyContainer.getSurvivorLeaderboardUseCase(
            contestId = contestId,
            statusFilter = statusFilter,
            limit = pageSize,
            offset = currentPage * pageSize
        ).fold(
            onSuccess = { leaderboardResult = it },
            onFailure = { error = it.message }
        )

        // Load user's entries
        DependencyContainer.getUserSurvivorEntriesUseCase(contestId).fold(
            onSuccess = { userEntries = it },
            onFailure = { /* User might not have entries */ }
        )

        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(Colors.Primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button(
                    text = "Back",
                    onClick = {
                        NavigationState.navigate(Route.SurvivorContestDetail(contestId))
                    },
                    variant = "secondary"
                )
                H2({
                    style {
                        color(Colors.White)
                        fontSize(20.px)
                        fontWeight(700)
                        margin(0.px)
                    }
                }) {
                    Text("Leaderboard")
                }
            }

            // Contest name and live indicator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                contest?.let {
                    Span({
                        style {
                            color(rgba(255, 255, 255, 0.8))
                            fontSize(14.px)
                        }
                    }) {
                        Text(it.name)
                    }
                }

                // Live indicator - only show when contest is ACTIVE and connected
                if (realtimeState.isConnected && contest?.status == SurvivorContestStatus.ACTIVE) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            backgroundColor(rgba(76, 175, 80, 0.2))
                            padding(4.px, 8.px)
                            borderRadius(12.px)
                        }
                    }) {
                        // Green dot
                        Div({
                            style {
                                width(8.px)
                                height(8.px)
                                borderRadius(50.percent)
                                backgroundColor(rgb(76, 175, 80))
                            }
                        })
                        Span({
                            style {
                                fontSize(11.px)
                                color(Colors.White)
                                fontWeight(500)
                            }
                        }) {
                            Text("LIVE")
                        }
                    }
                }
            }
        }

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.getSurvivorLeaderboardUseCase(contestId).fold(
                            onSuccess = { leaderboardResult = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                else -> {
                    // User's position highlight
                    if (userEntries.isNotEmpty()) {
                        UserPositionCard(userEntries, contestId, contest)
                    }

                    // Contest stats
                    contest?.let { c ->
                        leaderboardResult?.let { result ->
                            ContestStats(c, result)
                        }
                    }

                    // Status filter tabs
                    StatusFilterTabs(
                        selected = statusFilter,
                        onSelect = { statusFilter = it; currentPage = 0 },
                        aliveCount = leaderboardResult?.aliveCount ?: 0,
                        totalCount = leaderboardResult?.total ?: 0
                    )

                    // Leaderboard table
                    leaderboardResult?.let { result ->
                        LeaderboardTable(
                            entries = result.entries,
                            userEntryIds = userEntries.map { it.id }.toSet(),
                            contestId = contestId,
                            contest = contest
                        )
                    }

                    // Pagination
                    leaderboardResult?.let { result ->
                        if (result.entries.size >= pageSize || currentPage > 0) {
                            Pagination(
                                currentPage = currentPage,
                                hasMore = result.entries.size >= pageSize,
                                onPrevious = { if (currentPage > 0) currentPage-- },
                                onNext = { if (result.entries.size >= pageSize) currentPage++ }
                            )
                        }
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun UserPositionCard(
    userEntries: List<SurvivorEntryEntity>,
    contestId: String,
    contest: SurvivorContestEntity?
) {
    Div({
        style {
            backgroundColor(rgb(227, 242, 253))
            borderRadius(12.px)
            padding(20.px)
            marginBottom(24.px)
        }
    }) {
        H3({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(rgb(25, 118, 210))
                marginBottom(12.px)
                margin(0.px)
            }
        }) {
            Text("Your Entries")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            userEntries.forEach { entry ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        backgroundColor(Colors.White)
                        padding(12.px, 16.px)
                        borderRadius(8.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        // Status indicator
                        StatusIndicator(entry.status)

                        // Avatar
                        AvatarDisplay(
                            avatarUrl = entry.avatarUrl,
                            initials = entry.initials,
                            size = AvatarSize.SMALL
                        )

                        Div {
                            Div({
                                style {
                                    fontWeight(500)
                                    fontSize(14.px)
                                }
                            }) {
                                Text(entry.displayName)
                            }
                            Div({
                                style {
                                    fontSize(12.px)
                                    color(Colors.TextSecondary)
                                }
                            }) {
                                Text("Survived ${entry.survivedRounds} rounds")
                                contest?.let { c ->
                                    if (c.variation == SurvivorVariation.STRIKE_SYSTEM) {
                                        Text(" • ${entry.strikesUsed}/${c.strikeLimit} strikes")
                                    }
                                }
                            }
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(16.px)
                        }
                    }) {
                        // Rank if available
                        entry.rank?.let { rank ->
                            Div({
                                style {
                                    textAlign("right")
                                }
                            }) {
                                Div({
                                    style {
                                        fontSize(18.px)
                                        fontWeight(600)
                                        color(Colors.Primary)
                                    }
                                }) {
                                    Text("#$rank")
                                }
                            }
                        }

                        Button(
                            text = "View",
                            onClick = {
                                NavigationState.navigate(Route.SurvivorEntry(contestId, entry.id))
                            },
                            variant = "secondary"
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ContestStats(
    contest: SurvivorContestEntity,
    leaderboardResult: SurvivorLeaderboardResult
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(100px, 1fr))")
            gap(16.px)
            marginBottom(24.px)
        }
    }) {
        StatBox("Alive", "${leaderboardResult.aliveCount}", Colors.Success)
        StatBox("Eliminated", "${contest.eliminatedCount}", Colors.Error)
        StatBox("Total Entries", "${contest.currentEntries}", null)
        StatBox("Prize Pool", contest.prizePoolDisplay, null)
        contest.currentRound?.let {
            StatBox("Round", it.displayName, null)
        }
    }
}

@Composable
private fun StatBox(label: String, value: String, valueColor: CSSColorValue?) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            padding(16.px)
            borderRadius(8.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(4.px)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(valueColor ?: Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun StatusFilterTabs(
    selected: String?,
    onSelect: (String?) -> Unit,
    aliveCount: Int,
    totalCount: Int
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            marginBottom(16.px)
        }
    }) {
        FilterTab("All ($totalCount)", selected == null) { onSelect(null) }
        FilterTab("Alive ($aliveCount)", selected == "alive") { onSelect("alive") }
        FilterTab("Eliminated (${totalCount - aliveCount})", selected == "eliminated") { onSelect("eliminated") }
    }
}

@Composable
private fun FilterTab(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(8.px, 16.px)
            borderRadius(20.px)
            cursor("pointer")
            fontSize(14.px)
            fontWeight(if (isSelected) 600 else 400)
            backgroundColor(if (isSelected) Colors.Primary else Colors.Surface)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LeaderboardTable(
    entries: List<SurvivorEntryEntity>,
    userEntryIds: Set<String>,
    contestId: String,
    contest: SurvivorContestEntity?
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Table header
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "60px 1fr 100px 100px")
                padding(12.px, 16.px)
                backgroundColor(Colors.SurfaceVariant)
                property("border-bottom", "1px solid ${Colors.Gray300}")
                fontSize(12.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Span { Text("Rank") }
            Span { Text("Player") }
            Span({
                style { textAlign("center") }
            }) { Text("Status") }
            Span({
                style { textAlign("center") }
            }) { Text("Rounds") }
        }

        // Table rows
        if (entries.isEmpty()) {
            Div({
                style {
                    padding(32.px)
                    textAlign("center")
                    color(Colors.TextSecondary)
                }
            }) {
                Text("No entries yet")
            }
        } else {
            entries.forEachIndexed { index, entry ->
                val isUserEntry = entry.id in userEntryIds

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "60px 1fr 100px 100px")
                        padding(12.px, 16.px)
                        alignItems(AlignItems.Center)
                        backgroundColor(if (isUserEntry) rgb(227, 242, 253) else Colors.White)
                        property("border-bottom", "1px solid ${Colors.Gray100}")
                        cursor("pointer")
                        property("transition", "background-color 0.2s")
                    }
                    onClick {
                        NavigationState.navigate(Route.SurvivorEntry(contestId, entry.id))
                    }
                }) {
                    // Rank
                    RankBadge(index + 1, entry.status == SurvivorEntryStatus.ALIVE)

                    // Player with avatar
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        // Avatar
                        AvatarDisplay(
                            avatarUrl = entry.avatarUrl,
                            initials = entry.initials,
                            size = AvatarSize.SMALL
                        )

                        // Name and status
                        Div {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(6.px)
                                }
                            }) {
                                Span({
                                    style {
                                        fontWeight(if (isUserEntry) 600 else 500)
                                        fontSize(14.px)
                                        color(Colors.TextPrimary)
                                    }
                                }) {
                                    Text(entry.displayName)
                                }
                                if (isUserEntry) {
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            color(rgb(25, 118, 210))
                                            fontWeight(500)
                                        }
                                    }) {
                                        Text("(You)")
                                    }
                                }
                            }

                            // Last pick round info
                            entry.lastPickRound?.let { round ->
                                Div({
                                    style {
                                        fontSize(12.px)
                                        color(Colors.TextSecondary)
                                    }
                                }) {
                                    Text("Last pick: ${round.displayName}")
                                }
                            }
                        }
                    }

                    // Status
                    Div({
                        style {
                            textAlign("center")
                        }
                    }) {
                        StatusBadge(entry.status)
                        // Strike indicator
                        contest?.let { c ->
                            if (c.variation == SurvivorVariation.STRIKE_SYSTEM && entry.status == SurvivorEntryStatus.ALIVE) {
                                Div({
                                    style {
                                        fontSize(11.px)
                                        color(Colors.TextSecondary)
                                        marginTop(2.px)
                                    }
                                }) {
                                    Text("${entry.strikesUsed}/${c.strikeLimit} strikes")
                                }
                            }
                        }
                    }

                    // Survived rounds
                    Div({
                        style {
                            textAlign("center")
                            fontSize(16.px)
                            fontWeight(600)
                            color(Colors.Primary)
                        }
                    }) {
                        Text("${entry.survivedRounds}")
                    }
                }
            }
        }
    }
}

@Composable
private fun RankBadge(rank: Int, isAlive: Boolean) {
    val (bgColor, textColor) = when {
        !isAlive -> Colors.Gray200 to Colors.Gray600
        rank == 1 -> rgb(255, 215, 0) to rgb(0, 0, 0) // Gold
        rank == 2 -> rgb(192, 192, 192) to rgb(0, 0, 0) // Silver
        rank == 3 -> rgb(205, 127, 50) to Colors.White // Bronze
        else -> Colors.SurfaceVariant to Colors.TextPrimary
    }

    Div({
        style {
            width(36.px)
            height(36.px)
            borderRadius(50.percent)
            backgroundColor(bgColor)
            color(textColor)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(14.px)
            fontWeight(600)
        }
    }) {
        Text("$rank")
    }
}

@Composable
private fun StatusIndicator(status: SurvivorEntryStatus) {
    val color = when (status) {
        SurvivorEntryStatus.ALIVE -> Colors.Success
        SurvivorEntryStatus.WINNER, SurvivorEntryStatus.PAID -> rgb(255, 215, 0)
        else -> Colors.Error
    }

    Div({
        style {
            width(12.px)
            height(12.px)
            borderRadius(50.percent)
            backgroundColor(color)
        }
    })
}

@Composable
private fun StatusBadge(status: SurvivorEntryStatus) {
    val (bgColor, textColor, text) = when (status) {
        SurvivorEntryStatus.ALIVE -> Triple(rgb(232, 245, 233), Colors.Success, "Alive")
        SurvivorEntryStatus.ELIMINATED -> Triple(rgb(255, 235, 238), Colors.Error, "Out")
        SurvivorEntryStatus.REBUY_ELIGIBLE -> Triple(rgb(255, 243, 224), Colors.Warning, "Rebuy")
        SurvivorEntryStatus.WITHDRAWN -> Triple(Colors.Gray200, Colors.Gray600, "Left")
        SurvivorEntryStatus.WINNER -> Triple(rgb(255, 243, 224), Colors.Warning, "Winner")
        SurvivorEntryStatus.PAID -> Triple(rgb(232, 245, 233), Colors.Success, "Paid")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(12.px)
        }
    }) {
        Text(text)
    }
}

@Composable
private fun Pagination(
    currentPage: Int,
    hasMore: Boolean,
    onPrevious: () -> Unit,
    onNext: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(16.px)
            marginTop(24.px)
        }
    }) {
        Button(
            text = "Previous",
            onClick = onPrevious,
            variant = if (currentPage > 0) "secondary" else "disabled"
        )

        Span({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("Page ${currentPage + 1}")
        }

        Button(
            text = "Next",
            onClick = onNext,
            variant = if (hasMore) "secondary" else "disabled"
        )
    }
}
